// Global map for polygonOffset configurations
// Maps (factor, units) pairs to indices 0-63 for pipeline hash encoding

// Why global?
// It would be cleaner to have the depthBiasMap owned by a pipeline or renderer.
// However, since pipelineHashes are attached to materials for caching, we must ensure that the meaning of a
// depthBiasIndex keeps the same - no matter which UberPipeline instance (main vs. overlay) or renderer instances creates it.

// Avoiding the global map would require to manage pipelineHashes different than attaching too materials.
// But just doing this for nicer depthBias management seems an inappropriately invasive change.

const MAX_DEPTH_BIAS_CONFIGS = 64;

// Global singleton - shared by all pipelines
const globalDepthBiasMap = {
  // Index 0 is reserved for "no offset"
  configs: [{ depthBias: 0, depthBiasSlopeScale: 0 }],
  keyToIndex: new Map([["0,0", 0]])
};

/**
 * Registers a material's polygonOffset config and returns its index (0-63).
 * Index 0 means "no offset" (either polygonOffset=false or factor=units=0).
 * @param {Object} material
 * @returns {number} index 0-63
 */
export function registerDepthBias(material) {
  const depthBiasMap = globalDepthBiasMap;
  // No offset case
  if (!material.polygonOffset) {
    return 0;
  }

  const factor = material.polygonOffsetFactor ?? 0;
  const units = material.polygonOffsetUnits ?? 0;

  // Treat (0,0) as no offset
  if (factor === 0 && units === 0) {
    return 0;
  }

  const key = `${factor},${units}`;

  // Check if already registered
  let index = depthBiasMap.keyToIndex.get(key);
  if (index !== undefined) {
    return index;
  }

  // Register new config
  if (depthBiasMap.configs.length >= MAX_DEPTH_BIAS_CONFIGS) {
    // Restricting the number of allowed polygon offsets is a trade-off to allow efficient encoding of the depthBiasIndex in pipelineHashes.
    // If we ever run into this warning, we have to either check for an unnecessarily high count of different polygonOffsets, increase the limit,
    // or generalize the code to support more.
    console.warn(`Number of different depthBias configurations is limited to (max ${MAX_DEPTH_BIAS_CONFIGS}). Ignoring PolygonOffset:`, factor, units);
    return 0;
  }

  index = depthBiasMap.configs.length;

  // ═══════════════════════════════════════════════════════════════════════════
  // Depth Offset Formulas - WebGL/OpenGL vs WebGPU
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // WebGL/OpenGL (glPolygonOffset):
  //   offset = factor × m + r × units
  //   See: https://registry.khronos.org/OpenGL-Refpages/gl4/html/glPolygonOffset.xhtml
  //
  // WebGPU (GPUDepthStencilState):
  //   offset = depthBiasSlopeScale × m + r × depthBias
  //   See: https://www.w3.org/TR/webgpu/#depth-stencil-state
  //
  // Where:
  //   m = max(|∂z/∂x|, |∂z/∂y|)  (maximum depth slope of the polygon)
  //   r = smallest resolvable depth buffer increment
  //
  // Parameter mapping (same semantics, different names):
  //   OpenGL factor  ↔  WebGPU depthBiasSlopeScale
  //   OpenGL units   ↔  WebGPU depthBias
  //
  // Sign negation: In WebGPU, we use reversed-Z depth (near=1, far=0).
  //   (Before/WebGL/PolygonOffset) In forward-Z, positive offset pushes fragments away (larger depth value).
  //   (Now/WebGPU/depthBias)       In reversed-Z, "away" means smaller depth value, so we negate both.
  // ═══════════════════════════════════════════════════════════════════════════

  depthBiasMap.configs.push({
    depthBias: -units,
    depthBiasSlopeScale: -factor
  });
  depthBiasMap.keyToIndex.set(key, index);

  return index;
}

/**
 * Gets depthBias values directly from material's polygonOffset properties.
 * Use this when creating pipelines.
 * @param {Object} material
 * @returns {{depthBias: number, depthBiasSlopeScale: number}}
 */
export function getDepthBiasValues(material) {
  if (!material.polygonOffset) {
    return { depthBias: 0, depthBiasSlopeScale: 0 };
  }
  const units = material.polygonOffsetUnits ?? 0;
  const factor = material.polygonOffsetFactor ?? 0;
  // Negate for reversed-Z (see comment in registerDepthBias)
  return {
    depthBias: -units,
    depthBiasSlopeScale: -factor
  };
}